"use client";

/**
 * DateRangeFilter — 기간 필터 (날짜 유형 + 시작일 ~ 종료일 + 빠른 선택 프리셋)
 *
 * 거의 모든 관리자 필터에 들어가는 "기간" 영역을 공통화한 것.
 * AdminFilterField 를 품고 있어 AdminFilter 안에 바로 넣으면 된다. 제어 컴포넌트.
 * 날짜 입력은 AdminDateInput 을 사용한다.
 *
 * 날짜는 "YYYY-MM-DD" 문자열로 주고받는다.
 *
 * @example
 * <DateRangeFilter
 *   typeOptions={[{ value: "joinedAt", label: "가입일" }]}
 *   type={filters.dateType}
 *   onTypeChange={(v) => setField("dateType", v)}
 *   startDate={filters.startDate}
 *   endDate={filters.endDate}
 *   onStartDateChange={(v) => setField("startDate", v)}
 *   onEndDateChange={(v) => setField("endDate", v)}
 *   presets
 * />
 */
import type { ReactNode } from "react";

import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { AdminDateInput } from "../input/admin-date-input";
import { AdminFilterField } from "./admin-filter";

export interface DateTypeOption {
    value: string;
    label: string;
}

export interface DateRangePreset {
    label: string;
    /** 클릭 시 적용할 [시작일, 종료일] ("YYYY-MM-DD") 을 반환 */
    getRange: () => [string, string];
}

export interface DateRangeFilterProps {
    /** 필드 라벨 (기본 "기간") */
    label?: ReactNode;
    /** 날짜 유형 선택 옵션 (예: 가입일/수정일). 주면 좌측에 select 표시 */
    typeOptions?: DateTypeOption[];
    type?: string;
    onTypeChange?: (value: string) => void;
    /** 날짜 유형 select 의 "전체" 옵션 라벨. null 이면 표시 안 함 (기본 "전체") */
    allTypeLabel?: string | null;
    startDate: string | null;
    endDate: string | null;
    onStartDateChange: (value: string) => void;
    onEndDateChange: (value: string) => void;
    /**
     * 빠른 기간 선택 버튼.
     * - true: 기본 프리셋(오늘/최근 7일/최근 30일/최근 3개월)
     * - 배열: 커스텀 프리셋
     */
    presets?: boolean | DateRangePreset[];
    /** 시작일이 비었을 때 안내 문구 (기본 "날짜를 선택하세요") */
    startPlaceholder?: string;
    /** 종료일이 비었을 때 안내 문구 (기본 "날짜를 선택하세요") */
    endPlaceholder?: string;
    className?: string;
}

const selectClass =
    "h-9 shrink-0 rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50";

/** Date → "YYYY-MM-DD" */
function ymd(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

/** 오늘 기준 n일 전 날짜 */
function daysAgo(n: number): Date {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d;
}

/** 오늘 기준 n개월 전 날짜 */
function monthsAgo(n: number): Date {
    const d = new Date();
    d.setMonth(d.getMonth() - n);
    return d;
}

const DEFAULT_PRESETS: DateRangePreset[] = [
    { label: "오늘", getRange: () => [ymd(new Date()), ymd(new Date())] },
    { label: "최근 7일", getRange: () => [ymd(daysAgo(6)), ymd(new Date())] },
    { label: "최근 30일", getRange: () => [ymd(daysAgo(29)), ymd(new Date())] },
    { label: "최근 3개월", getRange: () => [ymd(monthsAgo(3)), ymd(new Date())] },
];

export function DateRangeFilter({
    label = "기간",
    typeOptions,
    type,
    onTypeChange,
    allTypeLabel = "전체",
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    presets,
    startPlaceholder = "날짜를 선택하세요",
    endPlaceholder = "날짜를 선택하세요",
    className,
}: DateRangeFilterProps) {
    const presetList =
        presets === true ? DEFAULT_PRESETS : Array.isArray(presets) ? presets : [];

    return (
        <AdminFilterField label={label} className={className}>
            <div className="flex flex-wrap items-center gap-2">
                {typeOptions && typeOptions.length > 0 && (
                    <select
                        aria-label="날짜 유형"
                        value={type ?? ""}
                        onChange={(e) => onTypeChange?.(e.target.value)}
                        className={cn(selectClass, "w-28")}
                    >
                        {allTypeLabel !== null && (
                            <option value="">{allTypeLabel}</option>
                        )}
                        {typeOptions.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                )}

                <AdminDateInput
                    aria-label="시작일"
                    value={startDate ?? ""}
                    max={endDate || undefined}
                    onChange={onStartDateChange}
                    placeholder={startPlaceholder}
                    className="w-56"
                />
                <span className="text-muted-foreground"> ~ </span>
                <AdminDateInput
                    aria-label="종료일"
                    value={endDate ?? ""}
                    min={startDate || undefined}
                    onChange={onEndDateChange}
                    placeholder={endPlaceholder}
                    className="w-56"
                />

                {presetList.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                        {presetList.map((preset) => (
                            <Button
                                key={preset.label}
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    const [start, end] = preset.getRange();
                                    onStartDateChange(start);
                                    onEndDateChange(end);
                                }}
                            >
                                {preset.label}
                            </Button>
                        ))}
                    </div>
                )}
            </div>
        </AdminFilterField>
    );
}
